import React, { useCallback, useMemo } from 'react';
import { Animated, Platform, StyleSheet, View, ViewProps } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Icon } from '@ui-kitten/components';

import Button from '../Components/Button';
import constants from '../Config/constants';
import useGoBack from '../Hooks/useGoBack';
import ThemeColor from '../Theme/colors';
import customMapping from '../Theme/mapping';
import ThemeStyle from '../Theme/styles';

const isAndroid34Below =
  constants.isAndroid && (Platform.Version as number) <= 34;

const createRgb = (opacity: number) => `rgba(255,255,255, ${opacity})`;

const Title = ({ children = ' ', ...rest }) => (
  <Animated.Text
    {...rest}
    style={[
      ThemeStyle.pageHorizontalSpacing, // will be overwritten
      ThemeStyle.spacingTopLarge, // will be overwritten
      styles.commonTitleStyle,
      styles.defaultTitleStyle,
      rest.style,
    ]}
    numberOfLines={1}
  >
    {children}
  </Animated.Text>
);

const HeaderWithTitleAnimation = ({
  accessoryRight,
  iconColor = ThemeColor.green,
  backType = 'back',
  scroll,
  title,
  withBanner,
  withBorder,
  onBackPress,
  ...rest
}: HeaderWithTitleAnimationProps) => {
  const goBack = useGoBack();
  const safearea = useSafeAreaInsets();

  const _createInterpolation = useCallback(
    (min, max, useMidInputRange = true) => {
      // condition is for: android 34 below status bar has calculated pixel, android 35+ status bar has 0 pixel
      const withBannerOutputRange =
        (isAndroid34Below ? -(safearea.top / 2) : 0) + 180;
      const maxOutputRange = withBanner ? withBannerOutputRange : 20; // calculated base on ui test
      return scroll.interpolate({
        inputRange: [
          0,
          useMidInputRange ? maxOutputRange / 2 : maxOutputRange - 1,
          maxOutputRange,
        ],
        outputRange: [min, min, max],
        extrapolate: 'clamp',
      });
    },
    [safearea.top, scroll, withBanner],
  );

  const containerInterpolatedStyle = useMemo(
    () => ({
      backgroundColor: _createInterpolation(createRgb(0), createRgb(1)),
      borderBottomWidth: withBorder ? _createInterpolation(0, 1) : null,
    }),
    [withBorder, _createInterpolation],
  );

  const titleInterpolatedStyle = useMemo(
    () => ({ opacity: _createInterpolation(0, 1, false) }),
    [_createInterpolation],
  );

  const _onBackPressed = () => {
    if (typeof onBackPress === 'function') {
      onBackPress();
    } else {
      goBack();
    }
  };

  return (
    <Animated.View
      {...rest}
      style={[
        styles.wrapper,
        containerInterpolatedStyle,
        withBanner && { paddingTop: safearea.top },
      ]}
    >
      <View style={[ThemeStyle.flex1, ThemeStyle.pageVerticalSpacingMedium]}>
        <View style={ThemeStyle.flexDirectionRowCenterSpaceBetween}>
          <Button
            testID="backButton"
            onPress={_onBackPressed}
            style={[ThemeStyle.flex3, ThemeStyle.flexDirectionRowCenter]}
            plain
          >
            {backType !== 'none' && (
              <View style={styles.clickableContentContainer}>
                <Icon
                  name={backType === 'back' ? 'arrow-back' : 'close'}
                  fill={iconColor}
                  style={styles.icon}
                />
              </View>
            )}

            <Title
              style={{
                fontSize: customMapping.strict['text-subtitle-2-font-size'],
                fontWeight: customMapping.strict['text-subtitle-2-font-weight'],
                marginTop: undefined,
                paddingHorizontal: undefined,
                ...titleInterpolatedStyle,
                ...ThemeStyle.spacingLeftSmall,
              }}
            >
              {title}
            </Title>
          </Button>

          {accessoryRight}
        </View>
      </View>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    position: 'absolute',
    width: '100%',
    top: 0,
    zIndex: 1,
    borderColor: ThemeColor.gray,
    ...ThemeStyle.pageHorizontalSpacingSmall,
  },
  clickableContentContainer: {
    backgroundColor: ThemeColor.white,
    padding: 5,
    borderRadius: 25,
  },
  icon: {
    width: 25,
    height: 25,
  },
  defaultTitleStyle: {
    opacity: 1,
    fontSize: customMapping.strict['text-heading-5-font-size'],
    fontWeight: customMapping.strict['text-heading-5-font-weight'],
  },
  commonTitleStyle: {
    color: '#222B45', // text-basic-color of eva theme, can't dynamically select it on eva theme
    fontFamily: customMapping.strict['text-font-family'],
    width: '95%',
  },
});

export type HeaderWithTitleAnimationProps = ViewProps & {
  accessoryRight?: React.ReactNode;
  backType: 'none' | 'back' | 'close';
  iconColor?: string;
  scroll: Animated.Value;
  title: string;
  withBanner?: boolean;
  withBorder?: boolean;
  onBackPress?: () => void;
};

export const navigationHeight = constants.isIOS ? 65 : 105;
export const AnimatedHeaderTitle = Title;
export default HeaderWithTitleAnimation;
